/**
 * The folder tree behind the page (D33, cap = 3).
 *
 * Drive nesting is capped at three levels below a pack, and each level has a
 * fixed home on the page: level 1 is the rail row, level 2 a section, level 3 a
 * group inside it. That is not an arbitrary number — measured against the live
 * drive, 639 of 717 published files already sit at three levels or shallower,
 * and the 78 that do not are two mis-filed subtrees in one pack. The cap is the
 * depth the content actually has, written down.
 *
 * The sync preflight rejects a fourth level outright (`max-depth`), so this
 * module should never meet one. It clamps anyway: a file below the cap is
 * attached to its level-3 ancestor and its remaining path is shown as the
 * file's subtitle. Content that arrived before the rule, or through a route
 * that skipped it, stays reachable rather than disappearing — the dashboard is
 * not the place to enforce a Drive convention.
 *
 * Every file already carries its full path (both server-side builders emit
 * `PACK/Folder/rest…`), so the tree is recoverable from the listing we already
 * fetch. Deriving it here rather than changing the response keeps the dashboard
 * and the function independently deployable — the same reason `foldersOf`
 * tolerates a server that predates `pack.folders`.
 */

/**
 * Folder levels a pack may contain: category / collection / unit.
 *
 * Shared with the server's rule of the same name by convention, not by import —
 * the dashboard and the function deploy separately, so a shared constant would
 * only be shared until one of them shipped.
 */
const MAX_FOLDER_DEPTH = 3

/** Files live under `PACK/Folder/…`; everything after that is tree structure. */
const PATH_PREFIX_SEGMENTS = 2

/**
 * A URL-safe segment slug, mirroring the server's `slugify` so a path built
 * here resolves the same way a server-built one would.
 */
function segmentSlug(name) {
  return String(name || '')
    .replace(/^\s*\d+[\s._-]+/, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** Display name with the ordering prefix removed, matching the server. */
function segmentName(name) {
  return String(name || '').replace(/^\s*\d+[\s._-]+/, '').trim()
}

function makeNode({ slug, name, slugs, generated = false }) {
  return { key: slugs.join('/'), slug, name, slugs, generated, files: [], children: [], count: 0 }
}

/**
 * Place one file at its directory path, creating nodes on the way down.
 *
 * The walk stops at the cap: `root` is level 1, so at most two more levels are
 * created below it. The *full* `dirs` stays on the file, which is what lets
 * `fileUnder` render the un-walked remainder as a subtitle instead of losing it.
 */
function insert(root, dirs, file) {
  let node = root
  dirs.slice(0, MAX_FOLDER_DEPTH - 1).forEach((dirName, index) => {
    const slug = segmentSlug(dirName) || `f${index}`
    let child = node.children.find(c => c.slug === slug)
    if (!child) {
      child = makeNode({ slug, name: segmentName(dirName) || dirName, slugs: [...node.slugs, slug] })
      node.children.push(child)
    }
    node = child
  })
  node.files.push({ ...file, dirs })
}

/**
 * Numeric-aware name comparison, so "Unit 10" follows "Unit 9" instead of
 * "Unit 1". Mirrors the server's `compareNatural` by convention, not import —
 * the dashboard and the function deploy separately.
 */
function compareNatural(a, b) {
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

/**
 * Sort every level by name, then roll subtree counts up from the leaves.
 *
 * The generated solutions folder is the exception: its children are missions,
 * which the server emits in course order. Sorting them by title would shuffle
 * the curriculum, so their order is kept as delivered.
 */
function finalize(node, keepChildOrder = false) {
  if (!keepChildOrder) {
    node.children.sort((a, b) => compareNatural(a.name, b.name))
  }
  node.files.sort((a, b) => compareNatural(a.name, b.name))
  node.count = node.files.length
  for (const child of node.children) {
    finalize(child)
    node.count += child.count
  }
  return node
}

/**
 * Turn the server's folder list into a tree, one root node per folder.
 *
 * The generated Code Solutions folder goes through the same path, so it nests
 * by mission exactly like a Drive folder does.
 */
function buildTree(folders) {
  return (folders || []).map((folder) => {
    const root = makeNode({
      slug: folder.slug,
      name: folder.name,
      slugs: [folder.slug],
      generated: Boolean(folder.generated),
    })
    for (const group of folder.groups || []) {
      for (const file of group.files || []) {
        const segments = String(file.path || '').split('/').slice(PATH_PREFIX_SEGMENTS)
        let dirs = segments.slice(0, -1)
        // The generated solutions folder groups by mission, and that label is
        // not always a path segment. Falling back to it keeps the mission level
        // in the tree instead of dropping every solution into one flat list.
        if (dirs.length === 0 && group.label && group.label !== folder.name) {
          dirs = [group.label]
        }
        insert(root, dirs, file)
      }
    }
    return finalize(root, root.generated)
  })
}

/**
 * The pack itself, as a node whose children are its folders.
 *
 * A bare `/educator-resources/pwr` means "the whole pack", and rendering that
 * through the same code path as a folder is what keeps the landing view showing
 * every folder at once — the property Jill asked for — with no second branch in
 * the page to keep in step.
 */
function packRootOf(roots, name) {
  const node = makeNode({ slug: '', name: name || 'All folders', slugs: [] })
  node.children = roots || []
  node.count = (roots || []).reduce((sum, child) => sum + child.count, 0)
  return node
}

/**
 * The folder a URL names, or null for the whole pack.
 *
 * A slug that no longer exists is not an error: Drive folders get renamed, and
 * a teacher following last term's bookmark should land on the pack rather than
 * an error page (D26). Returning null does exactly that, because the caller
 * renders null as the whole-pack view.
 *
 * Only the first segment is consulted. Links minted while the rail was a tree
 * carry deeper paths (`…/answer-keys/ap-csp-answer-keys/unit2`), and with the
 * cap in place there is no view below the folder to send them to — landing on
 * the folder they name is both the nearest truth and one scroll from the file.
 */
function folderBySlug(roots, slug) {
  if (!slug) {
    return null
  }
  return (roots || []).find(node => node.slug === slug) || null
}

/** Every file at or below a node, in stable order. */
function subtreeFiles(node) {
  if (!node) {
    return []
  }
  const out = [...node.files]
  for (const child of node.children) {
    out.push(...subtreeFiles(child))
  }
  return out
}

/**
 * Re-render a file for display beneath `node`, so its subtitle shows the path
 * remaining below whatever the reader is currently looking at rather than the
 * path below the folder root.
 */
function fileUnder(node, file) {
  const depth = node.slugs.length - 1
  const rest = (file.dirs || []).slice(depth)
  return { ...file, subtitle: rest.join(' / ') }
}

/**
 * The groups shown inside one section: one per immediate child, plus the
 * node's own loose files. With the cap in place a child is a level-3 unit, so
 * its group is a leaf — everything the folder holds is on the page at once,
 * which is the property that lets the rail stay flat.
 */
function groupsFor(node) {
  if (!node) {
    return []
  }
  const groups = node.children.map((child) => {
    const files = subtreeFiles(child).map(file => fileUnder(child, file))
    return {
      label: child.name,
      slugs: child.slugs,
      meta: `${files.length} file${files.length === 1 ? '' : 's'}`,
      files,
    }
  })
  if (node.files.length > 0) {
    const files = node.files.map(file => fileUnder(node, file))
    groups.push({
      label: node.children.length > 0 ? `In ${node.name}` : node.name,
      slugs: node.slugs,
      meta: `${files.length} file${files.length === 1 ? '' : 's'}`,
      // A collection with no units below it has one group, and its label is the
      // section heading verbatim. Rendering a collapsible header there would
      // print the same name twice and offer to hide a section from itself, so
      // the group is marked bare and drawn as a plain table.
      bare: node.children.length === 0,
      files,
    })
  }
  return groups
}

/**
 * The pane's sections for the selected node: one per immediate child, then the
 * node's own loose files. Called with a folder it yields that folder's level-2
 * collections; called with the pack root it yields the folders themselves, which
 * is the whole-pack landing view.
 */
function sectionsFor(node) {
  if (!node) {
    return []
  }
  const sections = node.children.map(child => ({
    key: child.key,
    slugs: child.slugs,
    name: child.name,
    generated: false,
    count: child.count,
    groups: groupsFor(child),
  }))
  if (node.files.length > 0) {
    sections.push({
      key: `${node.key}/·files`,
      slugs: node.slugs,
      name: node.children.length > 0 ? `In ${node.name}` : node.name,
      generated: node.generated,
      count: node.files.length,
      groups: [
        {
          label: node.name,
          slugs: node.slugs,
          meta: `${node.files.length} file${node.files.length === 1 ? '' : 's'}`,
          files: node.files.map(file => fileUnder(node, file)),
        },
      ],
    })
  }
  return sections
}

/** Total files across every root except the generated solutions folder. */
function documentTotalOf(roots) {
  return (roots || []).filter(n => !n.generated).reduce((sum, n) => sum + n.count, 0)
}

/** How many generated code solutions this pack has. */
function solutionsTotalOf(roots) {
  const found = (roots || []).find(n => n.generated)
  return found ? found.count : 0
}

export {
  MAX_FOLDER_DEPTH,
  buildTree,
  documentTotalOf,
  folderBySlug,
  groupsFor,
  packRootOf,
  sectionsFor,
  segmentName,
  segmentSlug,
  solutionsTotalOf,
  subtreeFiles,
}
